import { BLOCKS } from '@contentful/rich-text-types';
import React from 'react';
import { IImageLink, IPaymentOption } from '../../types/@types/generated/contentful';
import { ImpacterResponse } from '../../types/ImpacterTypes';
import { getCategory, GetCategoryResponse, getImpacters } from '../../utils/api';
import { getVideoById, Video } from '../../utils/api/videoApi';
import { Color, colors } from '../../utils/styleguide';
import HeroDonationLayout from '../component-library/layout/HeroSection/HeroDonationLayout';
import { variants } from '../component-library/text/Typography';
import Image, { ImageStyleProps } from './Image';
import { PageSectionComponent } from './pageSections';
import RichText from './RichText/RichText';
import pageToCompatibleProps from './util/pageToCompatibleProps';
import sectionToCompatibleProps from './util/sectionToCompatibleProps';
import sectionToCompatibleServerSideProps from './util/sectionToCompatibleServerSideProps';
import { getDonationProgramById } from '../../utils/api/partner';

type ServerSideProps = {
  portraitBackgroundVideo?: Video;
  landscapeBackgroundVideo?: Video;
  impacters?: Pick<ImpacterResponse, 'id' | 'externalId' | 'nickname'>[];
  category?: GetCategoryResponse;
  showKlarnaAttentionBanner?: boolean;
  paymentOption?: IPaymentOption;
  thankYouPageOverrideSlug?: string;
  affiliateName?: string;
  initiator?: string;
  donationProgramId?: string;
  partnerId?: string;
};

const HeroDonationSection: PageSectionComponent<ServerSideProps> = ({
  section,
  serverSideProps: {
    impacters,
    landscapeBackgroundVideo,
    portraitBackgroundVideo,
    category,
    thankYouPageOverrideSlug,
    showKlarnaAttentionBanner,
    paymentOption,
    affiliateName,
    initiator,
    donationProgramId,
    partnerId,
  },
  floatingHeader,
  categoryName,
}) => {
  const {
    fields: { help, references, settings, image: imageDeprecated },
  } = section;

  const {
    id,
    description,
    title,
    backgroundImage,
    rawBackgroundColor,
    backgroundImageLandscape,
    backgroundImagePortrait,
    backgroundOverlay,
  } = sectionToCompatibleProps({
    section,
  });

  const logosList = (references as IImageLink[])?.map((section) => ({
    title: section.fields.title || '',
    Image: (props: ImageStyleProps) => <Image asset={section.fields.image} {...props} />,
  }));

  const campaign = categoryName || initiator;

  return (
    <HeroDonationLayout
      id={id}
      title={
        (title && (
          <RichText
            centered
            document={title}
            textColor={colors[(settings?.titleColor as Color) || 'white']}
          />
        )) ||
        ''
      }
      description={
        (description && (
          <RichText
            centered
            document={description}
            textColor={colors.white}
            styleOverrides={{
              [BLOCKS.PARAGRAPH]: variants.body.css,
            }}
          />
        )) ||
        ''
      }
      name={
        (help && (
          <RichText
            centered
            document={help}
            textColor={colors[(settings?.helpColor as Color) || 'white']}
          />
        )) ||
        ''
      }
      impacters={impacters}
      category={category}
      campaign={campaign}
      affiliateName={affiliateName}
      portraitBackgroundVideo={portraitBackgroundVideo}
      landscapeBackgroundVideo={landscapeBackgroundVideo}
      image={(props: ImageStyleProps) => (
        <Image
          asset={{
            // imageDeprecated was used as background image
            landscape: backgroundImageLandscape || backgroundImage || imageDeprecated,
            portrait: backgroundImagePortrait,
          }}
          {...props}
        />
      )}
      backgroundColor={rawBackgroundColor}
      paymentOption={paymentOption}
      initiator={initiator as string} // have to force casting because of type limitation with ServerSideProps (fields have to be optional)
      logosList={logosList}
      plan={settings?.plan === 'one time' ? settings?.plan : undefined}
      thankYouPageSlugOverride={thankYouPageOverrideSlug}
      overlay={backgroundOverlay}
      showKlarnaAttentionBanner={showKlarnaAttentionBanner}
      floatingHeader={floatingHeader}
      donationProgramId={donationProgramId}
      partnerId={partnerId}
    />
  );
};

HeroDonationSection.getCustomServerSideProps = async ({ page, section }) => {
  const { tag } = section.fields.settings || {};

  const { affiliateName, initiator, donationProgramId } = pageToCompatibleProps({
    page,
    settings: section.fields.settings,
  });

  let impacters: ImpacterResponse[] | undefined = undefined;
  let category: GetCategoryResponse | undefined = undefined;

  if (page.fields.category) {
    category = await getCategory(page.fields.category.sys.id);
  } else {
    ({ result: impacters } = await getImpacters({
      anyTagNames: [page.fields.campaignTag || tag],
      fields: ['id', 'externalId', 'nickname', 'organisationId'],
    }));
  }

  let partnerId: string | undefined = undefined;
  if (donationProgramId) {
    const donationProgram = await getDonationProgramById(donationProgramId);
    partnerId = donationProgram.partnerId;
  }

  const { backgroundVideoIdLandscape, backgroundVideoIdPortrait } =
    sectionToCompatibleServerSideProps({ section });
  const [portraitBackgroundVideo, landscapeBackgroundVideo] = await Promise.all([
    getVideoById(backgroundVideoIdPortrait),
    getVideoById(backgroundVideoIdLandscape),
  ]);

  return {
    impacters,
    portraitBackgroundVideo,
    landscapeBackgroundVideo,
    category,
    showKlarnaAttentionBanner: page.fields?.showKlarnaAttentionBanner,
    thankYouPageOverrideSlug: page.fields.thankYouPageOverride?.fields.slug,
    paymentOption: page.fields.paymentOption,
    affiliateName,
    initiator,
    donationProgramId,
    partnerId,
  };
};

export default HeroDonationSection;
